import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { DateFnsAdapter, MAT_DATE_FNS_FORMATS } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { AccountGroup } from '@galaxy/account-group';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { getLocale } from '@vendasta/galaxy/utility/locale';
import { DurationPeriod, SalesOrdersService } from '@vendasta/sales-orders';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, filter, map, scan } from 'rxjs/operators';
import { CreateOrderDetailsForm } from './create-order-details.interface';

interface periodOption {
  label: string;
  value: number;
}

interface address {
  city: string;
  state: string;
  country: string;
  zip: string;
}

interface Duration {
  value: number;
  duration: DurationPeriod;
}

const DEFAULT_DURATION: Duration = {
  value: 1,
  duration: DurationPeriod.YEAR,
};

@Component({
  selector: 'orders-create-order-details',
  templateUrl: './create-order-details.component.html',
  styleUrls: ['./create-order-details.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: getLocale() },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
  ],
})
export class CreateOrderDetailsComponent implements OnInit, OnDestroy {
  readonly minDate = new Date(new Date().setHours(7));
  readonly periodOptions: Array<periodOption>;

  @Input() business: Pick<AccountGroup, 'externalIdentifiers' | 'napData' | 'accountGroupId'>;
  @Input() orderId: string;
  @Input() contractStartDate = this.minDate;
  @Input() formGroup: UntypedFormGroup;
  @Input() contractDuration: Duration;
  @Input() createMode = false;
  @Input() editingDisabled = false;

  protected editingDuration = false;
  readonly contractDurationValueControl = new FormControl(DEFAULT_DURATION.value, [
    Validators.required,
    Validators.min(1),
  ]);
  readonly contractStartControl = new FormControl(this.contractStartDate, [Validators.required]);
  readonly headerForm = new FormGroup({
    contractStartDate: this.contractStartControl,
    contractDuration: new FormGroup({
      value: this.contractDurationValueControl,
      duration: new FormControl(DEFAULT_DURATION.duration, [Validators.required]),
    }),
  });

  private readonly updating$$ = new BehaviorSubject<number>(0);
  public readonly isUpdating$ = this.updating$$.pipe(
    scan((acc, value) => acc + value),
    map((total) => total > 0),
  );
  private readonly addUpdateOp = () => this.updating$$.next(1);
  private readonly completeUpdateOp = () => this.updating$$.next(-1);

  address: address;

  private readonly subscriptions = new Subscription();

  constructor(private readonly salesOrdersService: SalesOrdersService, private readonly snack: SnackbarService) {
    this.periodOptions = this.initPeriodOptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.initHeaderForm();
    this.address = this.business?.napData;
  }

  private initHeaderForm(): void {
    this.formGroup.addControl('headerForm', this.headerForm);
    const durationControl = this.headerForm.controls.contractDuration;

    this.contractStartControl.setValue(this.contractStartDate);
    if (this.contractDuration) durationControl.setValue(this.contractDuration);

    if (this.editingDisabled === true) this.headerForm.disable();
    if (this.createMode === true) return;

    const dueTime = 1000;
    this.subscriptions.add(
      durationControl.valueChanges
        .pipe(
          filter(() => durationControl.valid),
          debounceTime(dueTime),
        )
        .subscribe(() => this.updateContractDuration()),
    );

    this.subscriptions.add(
      this.contractStartControl.valueChanges
        .pipe(
          filter(() => this.contractStartControl.valid),
          debounceTime(dueTime),
        )
        .subscribe(() => this.updateContractStartDate()),
    );
  }

  // used for editing existing order details (updates contract start date value specifically)
  private updateContractStartDate(): void {
    this.addUpdateOp();
    this.salesOrdersService
      .updateRequestedActivation(this.orderId, this.business.accountGroupId, this.contractStartControl.value)
      .toPromise()
      .catch(() => this.snack.openErrorSnack('COMMON.ORDER_DETAILS.CONTRACT_START_UPDATE_ERROR'))
      .finally(this.completeUpdateOp);
  }

  private updateContractDuration(): void {
    this.addUpdateOp();
    this.salesOrdersService
      .updateContractDuration(this.orderId, this.business.accountGroupId, this.headerForm.value.contractDuration)
      .toPromise()
      .catch(() => this.snack.openErrorSnack('COMMON.ORDER_DETAILS.CONTRACT_DURATION_UPDATE_ERROR'))
      .finally(this.completeUpdateOp);
  }

  private initPeriodOptions(): periodOption[] {
    return [
      {
        label: 'COMMON.ORDER_DETAILS.CONTRACT_DURATIONS.DAY',
        value: DurationPeriod.DAY,
      },
      {
        label: 'COMMON.ORDER_DETAILS.CONTRACT_DURATIONS.WEEK',
        value: DurationPeriod.WEEK,
      },
      {
        label: 'COMMON.ORDER_DETAILS.CONTRACT_DURATIONS.MONTH',
        value: DurationPeriod.MONTH,
      },
      {
        label: 'COMMON.ORDER_DETAILS.CONTRACT_DURATIONS.YEAR',
        value: DurationPeriod.YEAR,
      },
    ];
  }

  public getFormData(): CreateOrderDetailsForm {
    const data = this.headerForm.value;
    data.contractStartDate.setHours(7);
    return data as CreateOrderDetailsForm;
  }

  protected showEditableDuration(): void {
    const durationControl = this.headerForm.controls.contractDuration;
    if (!this.contractDuration) {
      durationControl.setValue(DEFAULT_DURATION);
    }

    this.editingDuration = true;
  }
}
